<template>
  <div class="app-container">
    <h1>Opinie użytkowników</h1>

    <!-- Stats Cards -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-value" :style="{ color: npsColor }">{{ stats.nps }}</div>
          <div class="stat-label">Wynik NPS</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-value">{{ stats.total_count }}</div>
          <div class="stat-label">Opinii (30 dni)</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-value">{{ stats.avg_rating }} ⭐</div>
          <div class="stat-label">Średnia ocena</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-value">{{ stats.today_count }}</div>
          <div class="stat-label">Dzisiaj</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- NPS Segments Bar -->
    <el-card style="margin-bottom: 20px;" shadow="hover">
      <div slot="header"><strong>Segmenty NPS (ostatnie 30 dni)</strong></div>
      <div class="nps-bar" v-if="stats.total_count > 0">
        <div
          class="nps-segment nps-critics"
          :style="{ width: criticsPercent + '%' }"
        >
          <span v-if="criticsPercent > 8">😞 {{ criticsPercent }}%</span>
        </div>
        <div
          class="nps-segment nps-neutrals"
          :style="{ width: neutralsPercent + '%' }"
        >
          <span v-if="neutralsPercent > 8">😐 {{ neutralsPercent }}%</span>
        </div>
        <div
          class="nps-segment nps-promoters"
          :style="{ width: promotersPercent + '%' }"
        >
          <span v-if="promotersPercent > 8">😍 {{ promotersPercent }}%</span>
        </div>
      </div>
      <div v-else style="text-align: center; color: #909399; padding: 20px;">
        Brak danych
      </div>
      <div class="nps-legend">
        <span><span class="legend-dot" style="background: #f56c6c;"></span> Krytycy (1-2): {{ stats.critics }}</span>
        <span><span class="legend-dot" style="background: #e6a23c;"></span> Neutralni (3): {{ stats.neutrals }}</span>
        <span><span class="legend-dot" style="background: #67c23a;"></span> Promotorzy (4-5): {{ stats.promoters }}</span>
      </div>
    </el-card>

    <!-- Rating Distribution -->
    <el-card style="margin-bottom: 20px;" shadow="hover">
      <div slot="header"><strong>Rozkład ocen</strong></div>
      <div class="distribution">
        <div v-for="r in [5,4,3,2,1]" :key="r" class="distribution-row">
          <span class="distribution-label">{{ ratingEmoji(r) }} {{ r }}</span>
          <el-progress
            :percentage="distributionPercent(r)"
            :color="ratingColor(r)"
            :stroke-width="20"
            :text-inside="true"
            :format="() => distribution[r] + ''"
            style="flex: 1;"
          ></el-progress>
        </div>
      </div>
    </el-card>

    <!-- Reviews Table -->
    <el-card shadow="hover">
      <div slot="header"><strong>Ostatnie opinie</strong></div>
      <el-table v-loading="loading" :data="reviews" style="width: 100%">
        <el-table-column label="Data" width="170">
          <template slot-scope="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Ocena" width="120">
          <template slot-scope="{ row }">
            {{ ratingEmoji(row.rating) }} {{ row.rating }}/5
          </template>
        </el-table-column>
        <el-table-column label="Segment" width="130">
          <template slot-scope="{ row }">
            <el-tag :type="segmentType(row.rating)" size="small">
              {{ segmentLabel(row.rating) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Komentarz" min-width="300">
          <template slot-scope="{ row }">
            {{ row.comment || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="Użytkownik" width="200">
          <template slot-scope="{ row }">
            {{ row.user ? row.user.email : 'Niezalogowany' }}
          </template>
        </el-table-column>
        <el-table-column label="" width="80" align="center">
          <template slot-scope="{ row }">
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              circle
              @click="handleDelete(row)"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>

      <div style="text-align: center; margin-top: 20px;">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="totalReviews"
          :page-size="perPage"
          :current-page.sync="currentPage"
          @current-change="fetchReviews"
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getReviews, deleteReview } from '@/api/reviews';

export default {
  name: 'ReviewsDashboard',
  data() {
    return {
      loading: false,
      reviews: [],
      totalReviews: 0,
      currentPage: 1,
      perPage: 20,
      stats: {
        nps: 0,
        total_count: 0,
        avg_rating: 0,
        today_count: 0,
        promoters: 0,
        neutrals: 0,
        critics: 0,
        distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      },
    };
  },
  computed: {
    distribution() {
      return this.stats.distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    },
    npsColor() {
      if (this.stats.nps > 50) return '#67c23a';
      if (this.stats.nps > 0) return '#e6a23c';
      return '#f56c6c';
    },
    criticsPercent() {
      if (!this.stats.total_count) return 0;
      return Math.round((this.stats.critics / this.stats.total_count) * 100);
    },
    neutralsPercent() {
      if (!this.stats.total_count) return 0;
      return Math.round((this.stats.neutrals / this.stats.total_count) * 100);
    },
    promotersPercent() {
      if (!this.stats.total_count) return 0;
      return Math.round((this.stats.promoters / this.stats.total_count) * 100);
    },
  },
  created() {
    this.fetchReviews();
  },
  methods: {
    async fetchReviews() {
      this.loading = true;
      try {
        const res = await getReviews({ page: this.currentPage, per_page: this.perPage });
        const data = res.data || res;
        this.reviews = data.reviews.data || data.reviews;
        this.totalReviews = data.reviews.total || 0;
        this.stats = data.stats;
      } catch (e) {
        this.$message.error('Nie udało się pobrać opinii');
      }
      this.loading = false;
    },
    async handleDelete(row) {
      try {
        await this.$confirm('Czy na pewno chcesz usunąć tę opinię?', 'Potwierdzenie', {
          confirmButtonText: 'Usuń',
          cancelButtonText: 'Anuluj',
          type: 'warning',
        });
        await deleteReview(row.id);
        this.$message.success('Opinia usunięta');
        this.fetchReviews();
      } catch (e) {
        // cancelled or error
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      var d = new Date(dateStr);
      return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
    ratingEmoji(rating) {
      var map = { 1: '😞', 2: '😐', 3: '😊', 4: '😍', 5: '🔥' };
      return map[rating] || '';
    },
    ratingColor(rating) {
      var map = { 1: '#f56c6c', 2: '#e6a23c', 3: '#909399', 4: '#409eff', 5: '#67c23a' };
      return map[rating] || '#409eff';
    },
    segmentType(rating) {
      if (rating >= 4) return 'success';
      if (rating === 3) return 'warning';
      return 'danger';
    },
    segmentLabel(rating) {
      if (rating >= 4) return 'Promotor';
      if (rating === 3) return 'Neutralny';
      return 'Krytyk';
    },
    distributionPercent(rating) {
      if (!this.stats.total_count) return 0;
      return Math.round(((this.distribution[rating] || 0) / this.stats.total_count) * 100);
    },
  },
};
</script>

<style scoped>
.stat-card {
  text-align: center;
  padding: 10px 0;
}
.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
}
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}
.nps-bar {
  display: flex;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}
.nps-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  transition: width 0.3s;
}
.nps-critics { background: #f56c6c; }
.nps-neutrals { background: #e6a23c; }
.nps-promoters { background: #67c23a; }
.nps-legend {
  display: flex;
  gap: 24px;
  justify-content: center;
  font-size: 13px;
  color: #606266;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}
.distribution {
  max-width: 500px;
}
.distribution-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.distribution-label {
  width: 50px;
  text-align: right;
  font-size: 14px;
}
</style>
